<template>
  <div v-if="editor">
    <div class="editor-icons-bar">
      <div class="editor-icons-bar--left">
        <button @click="editor.chain().focus().toggleBold().run()"
                :disabled="!editor.can().chain().focus().toggleBold().run()"
                :class="{ 'is-active': editor.isActive('bold') }"
                title="Полужирный">
          <font-awesome-icon icon="fa-solid fa-bold"/>
        </button>
        <button @click="editor.chain().focus().toggleItalic().run()"
                :disabled="!editor.can().chain().focus().toggleItalic().run()"
                :class="{ 'is-active': editor.isActive('italic') }"
                title="Курсив">
          <font-awesome-icon icon="fa-solid fa-italic"/>
        </button>
        <button @click="editor.chain().focus().toggleUnderline().run()"
                :class="{ 'is-active': editor.isActive('underline') }"
                title="Подчеркнутый">
          <font-awesome-icon icon="fa-solid fa-underline"/>
        </button>
        <button @click="editor.chain().focus().toggleStrike().run()"
                :disabled="!editor.can().chain().focus().toggleStrike().run()"
                :class="{ 'is-active': editor.isActive('strike') }"
                title="Зачеркнутый">
          <font-awesome-icon icon="fa-solid fa-strikethrough"/>
        </button>
        <button @click="editor.chain().focus().setParagraph().run()"
                :class="{ 'is-active': editor.isActive('paragraph') }"
                title="Параграф">
          <font-awesome-icon icon="fa-solid fa-paragraph"/>
        </button>
        <button @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
                title="Заголовок 1">
          <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
               viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve">
            <g>
            <path d="M293.1,447.6V64.4h-51.7v162.3H51.7V64.4H0v383.2h51.7V272.2h189.8v175.5H293.1z M512,447.6V64.4h-51.1L369.6,132v52
              l88.7-64.3h2.5v327.9H512z"/>
            </g>
          </svg>
        </button>
        <button @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                title="Заголовок 2">
          <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
               viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve">
            <g>
              <path d="M243.6,418.5V100h-42.9v134.9H43V100H0v318.5h42.9V272.7h157.7v145.8H243.6L243.6,418.5z M346.8,188.7V187
                c0-30.3,21.5-56.9,58.6-56.9c32.6,0,57.2,20.8,57.2,53.7c0,29.2-18.9,51.3-36.4,71.2L306.2,391.4v27.1H512v-37.3H367.1v-2.6
                l84.7-97.1c29.9-34.1,54.2-60.9,54.2-100.8c0-49.9-39.4-87.2-99.6-87.2C340,93.5,305,138.8,305,187v1.7H346.8z"/>
            </g>
          </svg>
        </button>

        <button @click="editor.chain().focus().toggleBulletList().run()"
                :class="{ 'is-active': editor.isActive('bulletList') }"
                title="Маркированный список">
          <font-awesome-icon icon="fa-solid fa-list-ul"/>
        </button>
        <button @click="editor.chain().focus().toggleOrderedList().run()"
                :class="{ 'is-active': editor.isActive('orderedList') }"
                title="Нумерованный список">
          <font-awesome-icon icon="fa-solid fa-list-ol"/>
        </button>
        <button @click="editor.chain().focus().toggleBlockquote().run()"
                :class="{ 'is-active': editor.isActive('blockquote') }"
                title="Цитата слева">
          <font-awesome-icon icon="fa-solid fa-quote-left"/>
        </button>
        <button @click="addImage()"
                :class="{ 'is-active': editor.isActive('image') }"
                title="Вставить/Редактировать ссылку на изображение">
          <font-awesome-icon icon="fa-solid fa-image"/>
        </button>
        <button @click="setLink()"
                :class="{ 'is-active': editor.isActive('link') }"
                title="Вставить/Редактировать ссылку">
          <font-awesome-icon icon="fa-solid fa-link"/>
        </button>
        <button @click="editor.chain().focus().unsetLink().run()"
                :disabled="!editor.isActive('link')"
                title="Удалить ссылку">
          <font-awesome-icon icon="fa-solid fa-chain-broken"/>
        </button>
        <div class="editor-icons-bar--left-formatting">
          <button @click="editor.chain().focus().setTextAlign('left').run()"
                  :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
                  title="По левому краю">
            <font-awesome-icon icon="fa-solid fa-align-left"/>
          </button>
          <button @click="editor.chain().focus().setTextAlign('center').run()"
                  :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
                  title="По центру">
            <font-awesome-icon icon="fa-solid fa-align-center"/>
          </button>
          <button @click="editor.chain().focus().setTextAlign('right').run()"
                  :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
                  title="По правому краю">
            <font-awesome-icon icon="fa-solid fa-align-right"/>
          </button>
          <button @click="editor.chain().focus().setTextAlign('justify').run()"
                  :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }"
                  title="По ширине">
            <font-awesome-icon icon="fa-solid fa-align-justify"/>
          </button>
          <div class="color-selector" title="Цвет текста">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" enable-background="new 0 0 24 24">
              <path d="M 10.90625 2 L 5.1875 17 L 7.90625 17 L 9.09375 13.5 L 14.90625 13.5 L 16.1875 17 L 18.90625 17 L 13.1875 2 L 10.90625 2 z M 12 5.1875 L 14.09375 11.40625 L 9.8125 11.40625 L 12 5.1875 z M 4 19 L 4 22 L 20 22 L 20 19 L 4 19 z"/>
            </svg>
            <input
                type="color"
                @input="editor.chain().focus().setColor($event.target.value).run()"
                :value="editor.getAttributes('textStyle').color"
            >
          </div>
          <div class="color-selector" title="Цвет фона">
            <svg enable-background="new 0 0 24 24" version="1.1" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" xmlns:cc="http://creativecommons.org/ns#" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
              <rect x=".70534" y=".86474" width="22.712" height="22.289" fill="#808080"/>
              <path d="m0.71716 12.001v-11.13h22.684v22.261h-22.684zm19.298 8.4923v-1.5094h-16.025v3.0189h16.025zm-11.866-4.1756c0.12732-0.37242 0.39408-1.1564 0.5928-1.7422l0.36132-1.0651h5.795l1.2741 3.4844 1.3744 0.0073c1.0957 0.0058 1.3717 1.13e-4 1.3612-0.02821-0.0072-0.01954-1.2941-3.3969-2.8598-7.5052l-2.8466-7.4697-2.3135 0.01428-2.8563 7.4907c-1.571 4.1199-2.8565 7.4941-2.8567 7.4981-1.975e-4 4e-3 0.61678 4e-3 1.3711 0l1.3715-0.0073z" fill="#ccc" stroke-width=".028214"/>
              <path d="m9.9717 10.993c1.0338-2.9639 2.0301-5.7539 2.0427-5.7203 0.06057 0.1613 2.0478 6.0792 2.0478 6.0983 0 0.01325-0.95193 0.02409-2.1154 0.02409h-2.1154z" fill="#ccc" stroke-width=".028214"/>
              <path d="m0.68895 12.001v-11.13h22.712v22.261h-22.712zm19.326 8.4923v-1.5094h-16.025v3.0189h16.025zm-11.497-5.2334 0.59249-1.7489 5.7826 0.0136 1.2709 3.4837 1.3787 3.3e-4c1.2993 3.11e-4 1.378-0.0025 1.3656-0.04904-0.01124-0.04243-5.5412-14.564-5.6567-14.855l-0.04208-0.1058h-2.3281l-0.3404 0.89579c-0.18722 0.4926-1.0087 2.6478-1.8255 4.7892-2.3002 6.0305-3.5408 9.2921-3.5408 9.3091 0 0.0085 0.61894 0.01552 1.3754 0.0155l1.3754-2.8e-5 0.59249-1.7489z" fill="#ccc" stroke-width=".028214"/>
              <path d="m10.906 2-5.7188 15h2.7188l1.1875-3.5h5.8125l1.2812 3.5h2.7188l-5.7188-15h-2.2812zm1.0938 3.1875 2.0938 6.2188h-4.2812l2.1875-6.2188zm-8 13.812v3h16v-3h-16z"/>
            </svg>
            <input
                type="color"
                @input="editor.chain().focus().setHighlight({color: `${$event.target.value}`}).run()"
                :value="editor.getAttributes('highlight').color"
            >
          </div>
          <div class="tables">
            <button @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
                    title="Вставить таблицу">
              <font-awesome-icon icon="fa-solid fa-table"/>
            </button>
            <button @click="editor.chain().focus().deleteTable().run()"
                    title="Удалить таблицу">
              <svg width="800px" height="800px" fill="#000000" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                <path d="m2 0h10c1.1046 0 2 0.89543 2 2v10c0 1.1046-0.89543 2-2 2h-10c-1.1046 0-2-0.89543-2-2v-10c0-1.1046 0.89543-2 2-2zm0 4v8h10v-8h-10zm5 5.4142-1.4142 1.4142c-0.39052 0.39052-1.0237 0.39052-1.4142 0-0.39052-0.39052-0.39052-1.0237 0-1.4142l1.4142-1.4142-1.4142-1.4142c-0.39052-0.39052-0.39052-1.0237 0-1.4142 0.39052-0.39052 1.0237-0.39052 1.4142 0l1.4142 1.4142 1.4142-1.4142c0.39052-0.39052 1.0237-0.39052 1.4142 0 0.39052 0.39052 0.39052 1.0237 0 1.4142l-1.4142 1.4142 1.4142 1.4142c0.39052 0.39052 0.39052 1.0237 0 1.4142-0.39052 0.39052-1.0237 0.39052-1.4142 0l-1.4142-1.4142z"/>
              </svg>
            </button>
            <button @click="editor.chain().focus().addColumnBefore().run()"
                    title="Добавить столбец слева">
              <svg enable-background="new 0 0 24 24" version="1.1" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" xmlns:cc="http://creativecommons.org/ns#" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
                <path d="m19 2c1.1 0 2 0.9 2 2v16c0 1.1-0.9 2-2 2h-8c-1.1 0-2-0.9-2-2v-2.0938c0.7-0.1 1.4-0.41875 2-0.71875v2.8125h8v-16h-8v2.8125c-0.6-0.4-1.3-0.61875-2-0.71875v-2.0938c0-1.1 0.9-2 2-2zm-11 5c2.8 0 5 2.2 5 5s-2.2 5-5 5-5-2.2-5-5 2.2-5 5-5zm1 2h-2v2h-2v2h2v2h2v-2h2v-2h-2z"/>
              </svg>
            </button>
            <button @click="editor.chain().focus().addColumnAfter().run()"
                    title="Добавить столбец справа">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" enable-background="new 0 0 24 24">
                <path d="M 5 2 C 3.9 2 3 2.9 3 4 L 3 20 C 3 21.1 3.9 22 5 22 L 13 22 C 14.1 22 15 21.1 15 20 L 15 17.90625 C 14.3 17.80625 13.6 17.4875 13 17.1875 L 13 20 L 5 20 L 5 4 L 13 4 L 13 6.8125 C 13.6 6.4125 14.3 6.19375 15 6.09375 L 15 4 C 15 2.9 14.1 2 13 2 L 5 2 z M 16 7 C 13.2 7 11 9.2 11 12 C 11 14.8 13.2 17 16 17 C 18.8 17 21 14.8 21 12 C 21 9.2 18.8 7 16 7 z M 15 9 L 17 9 L 17 11 L 19 11 L 19 13 L 17 13 L 17 15 L 15 15 L 15 13 L 13 13 L 13 11 L 15 11 L 15 9 z"/>
              </svg>
            </button>
            <button @click="editor.chain().focus().deleteColumn().run()"
                    title="Удалить столбец">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" enable-background="new 0 0 24 24">
                <path d="M 5 2 C 3.9 2 3 2.9 3 4 L 3 20 C 3 21.1 3.9 22 5 22 L 13 22 C 14.1 22 15 21.1 15 20 L 15 17.90625 C 14.3 17.80625 13.6 17.4875 13 17.1875 L 13 20 L 5 20 L 5 4 L 13 4 L 13 6.8125 C 13.6 6.4125 14.3 6.19375 15 6.09375 L 15 4 C 15 2.9 14.1 2 13 2 L 5 2 z M 16 7 C 13.2 7 11 9.2 11 12 C 11 14.8 13.2 17 16 17 C 18.8 17 21 14.8 21 12 C 21 9.2 18.8 7 16 7 z M 13 11 L 19 11 L 19 13 L 13 13 L 13 11 z"/>
              </svg>
            </button>
            <button @click="editor.chain().focus().addRowBefore().run()"
                    title="Добавить строку сверху">
              <svg enable-background="new 0 0 24 24" version="1.1" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" xmlns:cc="http://creativecommons.org/ns#" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
                <path d="m4 21c-1.1 0-2-0.9-2-2v-8c0-1.1 0.9-2 2-2h2.0938c0.1 0.7 0.41875 1.4 0.71875 2h-2.8125v8h16v-8h-2.8125c0.4-0.6 0.61875-1.3 0.71875-2h2.0938c1.1 0 2 0.9 2 2v8c0 1.1-0.9 2-2 2zm8-8c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5zm-1-2h2v-2h2v-2h-2v-2h-2v2h-2v2h2z"/>
              </svg>
            </button>
            <button @click="editor.chain().focus().addRowAfter().run()"
                    title="Добавить строку снизу">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" enable-background="new 0 0 24 24">
                <path d="M 4 3 C 2.9 3 2 3.9 2 5 L 2 13 C 2 14.1 2.9 15 4 15 L 6.09375 15 C 6.19375 14.3 6.5125 13.6 6.8125 13 L 4 13 L 4 5 L 20 5 L 20 13 L 17.1875 13 C 17.5875 13.6 17.80625 14.3 17.90625 15 L 20 15 C 21.1 15 22 14.1 22 13 L 22 5 C 22 3.9 21.1 3 20 3 L 4 3 z M 12 11 C 9.2 11 7 13.2 7 16 C 7 18.8 9.2 21 12 21 C 14.8 21 17 18.8 17 16 C 17 13.2 14.8 11 12 11 z M 11 13 L 13 13 L 13 15 L 15 15 L 15 17 L 13 17 L 13 19 L 11 19 L 11 17 L 9 17 L 9 15 L 11 15 L 11 13 z"/>
              </svg>
            </button>
            <button @click="editor.chain().focus().deleteRow().run()"
                    title="Удалить строку">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" enable-background="new 0 0 24 24">
                <path d="M 4 3 C 2.9 3 2 3.9 2 5 L 2 13 C 2 14.1 2.9 15 4 15 L 6.09375 15 C 6.19375 14.3 6.5125 13.6 6.8125 13 L 4 13 L 4 5 L 20 5 L 20 13 L 17.1875 13 C 17.5875 13.6 17.80625 14.3 17.90625 15 L 20 15 C 21.1 15 22 14.1 22 13 L 22 5 C 22 3.9 21.1 3 20 3 L 4 3 z M 12 11 C 9.2 11 7 13.2 7 16 C 7 18.8 9.2 21 12 21 C 14.8 21 17 18.8 17 16 C 17 13.2 14.8 11 12 11 z M 9 15 L 15 15 L 15 17 L 9 17 L 9 15 z"/>
              </svg>
            </button>
<!--            <button @click="editor.chain().focus().mergeCells().run()">-->
<!--              <svg width="800px" height="800px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">-->
<!--                <g>-->
<!--                  <path fill="none" d="M0 0H24V24H0z"/>-->
<!--                  <path d="M20 3c.552 0 1 .448 1 1v16c0 .552-.448 1-1 1H4c-.552 0-1-.448-1-1V4c0-.552.448-1 1-1h16zm-9 2H5v14h6v-4h2v4h6V5h-6v4h-2V5zm4 4l3 3-3 3v-2H9v2l-3-3 3-3v2h6V9z"/>-->
<!--                </g>-->
<!--              </svg>-->
<!--            </button>-->
<!--            <button @click="editor.chain().focus().splitCell().run()">-->
<!--              spc-->
<!--            </button>-->
          </div>
        </div>
      </div>
      <div class="editor-icons-bar--right">
        <button @click="editor.chain().focus().undo().run()" :disabled="!editor.can().chain().focus().undo().run()"
                title="Вернуть">
          <font-awesome-icon icon="fa-solid fa-undo"/>
        </button>
        <button @click="editor.chain().focus().redo().run()" :disabled="!editor.can().chain().focus().redo().run()"
                title="Отменить">
          <font-awesome-icon icon="fa-solid fa-redo"/>
        </button>
      </div>
    </div>
    <div class="ProseMirror-wrapper">
      <editor-content :editor="editor"/>
    </div>
  </div>
</template>

<script>
import {Editor, EditorContent} from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import {library} from '@fortawesome/fontawesome-svg-core'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {Color} from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Text from '@tiptap/extension-text'

import {
  faBold, faItalic, faStrikethrough, faCode, faMarker, faRedo, faUndo,
  faListOl, faListUl, faQuoteLeft, faHeading, faParagraph, faUnderline,
  faImage, faLink, faAlignJustify, faAlignRight, faAlignCenter, faAlignLeft,
  faTextHeight, faChainBroken, faTable
} from '@fortawesome/free-solid-svg-icons'

library.add([faBold, faItalic, faStrikethrough, faCode, faMarker, faParagraph, faUnderline, faHeading,
  faQuoteLeft, faListOl, faListUl, faRedo, faUndo, faImage, faLink,
  faAlignLeft, faAlignCenter, faAlignRight, faAlignJustify, faChainBroken, faTable])

export default {
  name: "TipTapEditor",
  components: {
    EditorContent,
    FontAwesomeIcon,
  },

  props: {
    value: {
      type: String,
      default: '',
    }
  },

  data() {
    return {
      editor: null,
    }
  },

  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value
      if (isSame) {
        return
      }

      this.editor.commands.setContent(value, false)
    }
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Underline,
        Image,
        Link,
        Color,
        Highlight.configure({
          multicolor: true
        }),
        TextStyle,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Table.configure({
          resizable: true,
        }),
        TableCell, TableRow, TableHeader, Text
      ],
      onUpdate: () => {
        //HTML
        this.$emit('editorUpdated', this.editor.getHTML())

        //JSON
        // this.$emit('input', this.editor.getJSON())
      },
    })
  },
  methods: {
    addImage() {
      const url = window.prompt('URL')

      if (url) {
        this.editor.chain().focus().setImage({src: url}).run()
      }
    },
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .unsetLink()
            .run()

        return
      }

      // update link
      this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({href: url})
          .run()
    },
  },
  beforeDestroy() {
    this.editor.destroy()
  },
}
</script>

<style lang="scss">

.ProseMirror-wrapper {
  padding: 5px 10px;
  height: 100%;
  overflow-y: scroll;
  background-color: white;
  border-radius: 0 0 5px 5px;
  border: 1px solid grey;
}

.ProseMirror {
}

.ProseMirror-focused {
  outline: none;
}

.ProseMirror {
  > * + * {
    margin-top: 0.75em;
  }

  ul,
  ol {
    padding: 0 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
  }

  code {
    background-color: rgba(#616161, 0.1);
    color: #616161;
  }

  pre {
    background: #0D0D0D;
    color: #FFF;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }

  img {
    max-width: 100%;
    height: auto;

    &.ProseMirror-selectednode {
      outline: 3px solid #68CEF8;
    }
  }

  blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0D0D0D, 0.1);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(#0D0D0D, 0.1);
    margin: 2rem 0;
  }

  a {
    color: #68CEF8;
  }

  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: bold;
      text-align: left;
      background-color: #f1f3f5;
    }

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: "";
      left: 0; right: 0; top: 0; bottom: 0;
      background: rgba(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  }

  p {
    margin: 0;
  }

  ul, ol {
    margin-bottom: 16px;
  }
}
.tableWrapper {
  padding: 1rem 0;
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
.editor-icons-bar {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 10px;

  &--left,
  &--left-formatting,
  &--right {
    display: flex;
    justify-content: start;
    flex-wrap: wrap;
    gap: 10px;
  }

  &--left-formatting {
    margin-left: 20px;

    .color-selector {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 66px;
      height: 33px;
      padding: 6px;
      border-radius: 3px;
      border: 1px solid #cdcdcd;
      color: #333;
      svg {
        height: 32px;
      }
      input {
        cursor: pointer;
      }
    }

    .tables {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: 20px;
      color: #333;
      gap: 5px;

      svg {
        height: 22px;
      }

      input {
        cursor: pointer;
      }

    }
  }

  button {
    display: flex;
    font-size: 18px;
    color: #333;
    justify-content: center;
    align-items: center;
    width: 33px;
    height: 33px;
    border-radius: 3px;
    border: 1px solid #cdcdcd;
    cursor: pointer;
  }

  button.is-active {
    background-color: #cdcdcd;
    border: 1px solid #bebebe;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/TipTapEditor/src/TipTapEditor.vue